// AI-ASSISTED: generated with claude-code on 2026-09-18, reviewed by <dev>
//
// Local copy constants for the Maintenance Windows feature (Constitution
// Polish task, spec research.md R1): English-only, no i18n framework wired —
// centralizing every user-facing string here keeps a future i18n pass (real
// translation refs) a cheap swap instead of a repo-wide hunt. Values are
// grouped by the component that owns them; template values are functions,
// everything else a plain string.

/** GrowthBook flag gating the whole area (mirrors backend MAINTENANCE_FLAG in maintenance-plugin.ts). */
export const MAINTENANCE_FLAG_NAME = 'backstage-maintenance-view';

/** Shared across every page's disabled state (SchedulesPage/ApplicationsPage/ApplicationHistoryPage). */
export const disabledNoticeText =
  'Maintenance Windows is not available. Ask a platform engineer to enable the ';
export const disabledNoticeSuffix = ' GrowthBook flag.';

export const COPY = {
  sidebar: {
    label: 'Maintenance Windows',
  },
  schedulesPage: {
    title: 'Maintenance Windows',
    subtitle: 'View and manage maintenance schedules across the fleet',
    applicationsLink: 'Applications registry',
    planButton: 'Plan Schedule',
    loadError: (message: string) => `Could not load maintenance schedules: ${message}`,
    emptyTitle: 'No maintenance schedules found',
    emptyDescription: 'No schedules match the current filters.',
    tableLabel: 'Maintenance schedules',
    columns: {
      application: 'Application',
      status: 'Status',
      start: 'Start',
      end: 'End',
      timeline: 'Timeline',
      scheduledBy: 'Scheduled by',
      actions: 'Actions',
    },
    actionsAriaLabel: (applicationName: string) => `Actions for ${applicationName}`,
    cancelAction: 'Cancel',
    cancelActionAriaLabel: (applicationName: string) =>
      `Cancel maintenance window for ${applicationName}`,
  },
  statCards: {
    activeNow: 'Active now',
    planned: 'Planned',
    completed: 'Completed',
    applications: 'Applications',
    summaryGroupLabel: 'Maintenance summary counts',
  },
  schedulesFilters: {
    searchLabel: 'Search by application name',
    applicationLabel: 'Filter by application',
    allApplications: 'All applications',
    statusLabel: 'Filter by status',
    allStatuses: 'All statuses',
    fromDateLabel: 'From date',
    untilDateLabel: 'Until date',
    refreshAriaLabel: 'Refresh maintenance schedules',
  },
  quickRangeMenu: {
    trigger: 'Quick range',
    back: 'Back',
    presetLabel: (offset: string, period: string) => `${offset} ${period.toLowerCase()}`,
  },
  planScheduleDialog: {
    title: 'Plan a maintenance window',
    timezoneHint: 'Start and end times are entered in Europe/Brussels time.',
    applicationLabel: 'Application',
    startLabel: 'Start',
    endLabel: 'End',
    durationPrefix: 'Duration: ',
    invalidRange: 'The end time must be after the start time.',
    conflictWarning: (count: number, applicationName: string) =>
      `This overlaps ${count} existing window${count > 1 ? 's' : ''} for ${applicationName}. You can still submit — the service will confirm at creation time.`,
    nowButton: 'Now',
    plusOneHourButton: '+1h',
    plusTwoHoursButton: '+2h',
    cancelButton: 'Cancel',
    submitButton: 'Plan window',
    submittingButton: 'Planning…',
  },
  cancelScheduleDialog: {
    title: 'Cancel this maintenance window?',
    // Split around the bolded application name (kept as JSX <strong> at the call site).
    bodyPrefix: 'This will cancel the window for ',
    bodySuffix: '. This action cannot be undone.',
    keepButton: 'Keep window',
    confirmButton: 'Cancel window',
    confirmingButton: 'Cancelling…',
  },
  applicationsPage: {
    title: 'Applications',
    subtitle: "The MyLuminus fleet's registered applications",
    backLink: '← Back to schedules',
    loadError: (message: string) => `Could not load applications: ${message}`,
    summaryGroupLabel: 'Applications summary',
    applicationsCount: (count: number) => `${count} applications`,
    applicationsCountAriaLabel: (count: number) => `Applications: ${count}`,
    downCount: (count: number) => `${count} DOWN`,
    downCountAriaLabel: (count: number) => `Down: ${count}`,
    searchLabel: 'Search by application name',
    statusLabel: 'Filter by status',
    allStatuses: 'All statuses',
    emptyTitle: 'No applications found',
    emptyDescription: 'No registered applications match the current filters.',
    tableLabel: 'Registered applications',
    columns: {
      application: 'Application',
      status: 'Status',
      healthCheck: 'Health check',
      dependencies: 'Dependencies',
      actions: 'Actions',
    },
    editAriaLabel: (applicationName: string) => `Edit application ${applicationName}`,
  },
  editApplicationDialog: {
    title: (applicationName: string) => `Edit ${applicationName}`,
    nameLabel: 'Application name',
    statusLabel: 'Status',
    healthCheckLabel: 'Health check URL',
    healthCheckInvalid: 'Enter a valid URL, or leave this field empty.',
    dependenciesHeading: 'Dependencies',
    noDependencies: 'No dependencies.',
    removeDependencyAriaLabel: (dependencyName: string) => `Remove dependency ${dependencyName}`,
    addDependencyLabel: 'Add dependency',
    noAvailableDependencies: 'No other applications to depend on.',
    addButton: 'Add',
    downDependencyWarning: (applicationName: string, dependencies: string[]) =>
      `${applicationName} depends on ${dependencies.join(', ')}, which ${dependencies.length > 1 ? 'are' : 'is'} currently DOWN — ${applicationName} may be affected.`,
    cancelButton: 'Cancel',
    saveButton: 'Save',
    savingButton: 'Saving…',
  },
  applicationHistoryPage: {
    fallbackTitle: 'Application history',
    subtitle: 'Maintenance window history',
    backLink: '← Back to applications',
    loadError: (applicationName: string, message: string) =>
      `Could not load the maintenance history for ${applicationName}: ${message}`,
    tabsAriaLabel: 'Filter by status',
    yearLabel: 'Filter by year',
    allYears: 'All years',
    emptyTitle: 'No maintenance windows found',
    emptyDescription: (applicationName: string) =>
      `${applicationName} has no maintenance windows matching the current filters.`,
    tableLabel: (applicationName: string) => `Maintenance history for ${applicationName}`,
    columns: {
      status: 'Status',
      start: 'Start',
      end: 'End',
      timeline: 'Timeline',
      scheduledBy: 'Scheduled by',
    },
    loadMoreButton: 'Load more',
  },
  timelineBar: {
    pastMidnight: 'past midnight',
    tooltip: (startLabel: string, endLabel: string) =>
      `Window runs from ${startLabel} to ${endLabel} Europe/Brussels time`,
  },
} as const;
